/**
 * Rendering a firewall policy into an nftables ruleset.
 *
 * The controller decides policy; this turns a resolved policy into the exact
 * `nft` script the edge loads. Three rules keep it safe by construction, and they
 * are the whole reason this is one deterministic function rather than string
 * concatenation scattered around:
 *
 *   - It only ever touches one dedicated table, `inet connaro`. The script deletes
 *     and recreates that table and nothing else, so an edge that shares its host
 *     with other firewall rules — an integration deployment — keeps them. It never
 *     emits `flush ruleset`.
 *   - The forward chain defaults to drop, so inter-site traffic no policy allowed is
 *     denied rather than leaked. Established and related flows are accepted first so
 *     a default-deny does not tear down connections the policy already permitted.
 *   - Management reachability is asserted ahead of every user rule. A deny rule can
 *     never lock the edge away from the controller, because the accepts for the
 *     management prefixes come first and nothing below can override them.
 *
 * Rules are rendered in priority order, and the output is deterministic, so the
 * same policy always produces the same script and a controller diffing two
 * revisions sees a change only when the policy actually changed.
 */

/** The zones a policy segments traffic into. Zones are resolved to concrete
 * prefixes by the controller before rendering; they travel on a rule as metadata
 * for display and audit, not as something matched here. */
export const FIREWALL_ZONES = [
  'WAN',
  'LAN',
  'VPN',
  'MANAGEMENT',
  'GUEST',
  'IOT',
  'SERVER',
  'VOICE',
  'TRANSIT',
  'RELAY',
  'CUSTOM',
] as const;
export type FirewallZone = (typeof FIREWALL_ZONES)[number];

/** What a matched rule does. Log/rate-limit/mark/QoS are policy extensions layered
 * on these base dispositions later. */
export const FIREWALL_ACTIONS = ['ALLOW', 'DENY', 'REJECT'] as const;
export type FirewallAction = (typeof FIREWALL_ACTIONS)[number];

export const FIREWALL_PROTOCOLS = ['ANY', 'TCP', 'UDP', 'ICMP'] as const;
export type FirewallProtocol = (typeof FIREWALL_PROTOCOLS)[number];

/** One resolved rule: concrete selectors, no zones to look up. */
export interface FirewallRule {
  /** Lower renders first; ties broken by id for stability. */
  priority: number;
  id: string;
  /** Human-readable, emitted as a comment so a live ruleset is legible. */
  description: string;
  /**
   * Source/destination prefixes, already resolved from any zones the rule named
   * plus any explicit prefix. An empty or absent list means any: a zone that
   * resolved to several prefixes renders as an nft set, so one rule stays one rule.
   */
  source?: string[];
  dest?: string[];
  protocol: FirewallProtocol;
  /** Destination port, for TCP/UDP; absent means any port. */
  destPort?: number;
  /** The base disposition: allow, deny, or reject. */
  action: FirewallAction;

  // Extended actions. These modify a matched packet before the verdict rather than
  // replacing it, so a rule can log-and-allow or mark-and-allow in one line. They
  // are rendered in a fixed order so the ruleset stays deterministic.

  /**
   * When set, only this many matched packets per second get the verdict; packets
   * over the rate fall through to the next rule. This is how a rule rate-limits —
   * `limit rate N/second accept` accepts up to N/s and lets the rest be decided
   * below.
   */
  rateLimitPerSecond?: number;
  /** Log matched packets (with a fixed, injection-safe prefix) before the verdict. */
  log?: boolean;
  /** Set a firewall mark on matched packets, for policy routing, before the verdict. */
  mark?: number;
  /** Set the DSCP field (0-63) for QoS classification, before the verdict. */
  dscp?: number;
}

/** A device's resolved firewall policy. */
export interface FirewallPolicy {
  rules: FirewallRule[];
  /** Prefixes that must always be reachable — the controller and any management
   * network. Accepts for these are emitted ahead of every user rule so no policy
   * can lock the edge out of its own management path. */
  managementCidrs: string[];
}

/** The dedicated table every rule lives in; nothing outside it is ever touched. */
export const FIREWALL_TABLE = 'inet connaro';

/** Escape a description for an nft comment: quotes and newlines would break the
 * script or smuggle in rules. */
function comment(text: string): string {
  const safe = text.replace(/["\n\r]/g, ' ').slice(0, 128);
  return safe.length > 0 ? ` comment "${safe}"` : '';
}

/** An address match for a set of prefixes: a bare address for one, an nft set for
 * several. Prefixes are sorted so the output is deterministic. Returns '' for none
 * (matches any). */
function addrMatch(field: 'saddr' | 'daddr', prefixes: string[] | undefined): string {
  if (!prefixes || prefixes.length === 0) return '';
  const sorted = [...new Set(prefixes)].sort();
  if (sorted.length === 1) return `ip ${field} ${sorted[0]}`;
  return `ip ${field} { ${sorted.join(', ')} }`;
}

/** The match clause for a rule's selectors, in a fixed field order so the output
 * is deterministic. Returns '' when the rule matches everything. */
function match(rule: FirewallRule): string {
  const parts: string[] = [];
  const src = addrMatch('saddr', rule.source);
  if (src) parts.push(src);
  const dst = addrMatch('daddr', rule.dest);
  if (dst) parts.push(dst);
  if (rule.protocol === 'TCP' || rule.protocol === 'UDP') {
    const proto = rule.protocol.toLowerCase();
    parts.push(`ip protocol ${proto}`);
    if (rule.destPort !== undefined) parts.push(`${proto} dport ${rule.destPort}`);
  } else if (rule.protocol === 'ICMP') {
    parts.push('ip protocol icmp');
  }
  return parts.join(' ');
}

/** The nft verdict for an action. */
function verdict(action: FirewallAction): string {
  switch (action) {
    case 'ALLOW':
      return 'accept';
    case 'REJECT':
      return 'reject';
    case 'DENY':
    default:
      return 'drop';
  }
}

/**
 * Render a policy into the nft script the edge applies.
 *
 * The script is add/delete/add over the dedicated table only: `add table` makes
 * the delete safe when the table does not exist yet, `delete table` removes our
 * previous ruleset entirely, and the rest rebuilds it — all in one file, so `nft`
 * applies it as a single atomic transaction that leaves every other table alone.
 */
export function renderNftables(policy: FirewallPolicy): string {
  const lines: string[] = [
    `add table ${FIREWALL_TABLE}`,
    `delete table ${FIREWALL_TABLE}`,
    `add table ${FIREWALL_TABLE}`,
    // Default drop: anything not explicitly allowed by a rule below is denied.
    `add chain ${FIREWALL_TABLE} forward { type filter hook forward priority filter; policy drop; }`,
    // Keep established flows alive so a default-deny does not sever permitted ones.
    `add rule ${FIREWALL_TABLE} forward ct state established,related accept`,
    `add rule ${FIREWALL_TABLE} forward ct state invalid drop`,
  ];

  // Management reachability is asserted before any user rule, both directions, so
  // no deny can strand the edge from its controller.
  for (const cidr of policy.managementCidrs) {
    lines.push(`add rule ${FIREWALL_TABLE} forward ip saddr ${cidr} accept comment "management"`);
    lines.push(`add rule ${FIREWALL_TABLE} forward ip daddr ${cidr} accept comment "management"`);
  }

  const ordered = [...policy.rules].sort(
    (a, b) => a.priority - b.priority || a.id.localeCompare(b.id),
  );
  for (const rule of ordered) {
    lines.push(`add rule ${FIREWALL_TABLE} forward ${ruleBody(rule)}${comment(rule.description)}`);
  }

  return lines.join('\n') + '\n';
}

/**
 * The statements of one rule, in a fixed order: what it matches, then the
 * modifiers that run on a match, then the verdict.
 *
 * The order is load-bearing. `limit rate` is a match — over-rate packets do not
 * match the rule and fall through — so it comes before the modifiers and verdict.
 * Logging and the mark and DSCP sets are non-terminal: they run and then execution
 * continues to the verdict, so a rule can log-and-allow or mark-and-allow. The
 * verdict is always last. Numeric modifiers are integers validated upstream, so
 * they cannot smuggle anything into the script; the log prefix is a fixed string.
 */
function ruleBody(rule: FirewallRule): string {
  const parts: string[] = [];
  const clause = match(rule);
  if (clause) parts.push(clause);
  if (rule.rateLimitPerSecond !== undefined && rule.rateLimitPerSecond > 0) {
    parts.push(`limit rate ${Math.floor(rule.rateLimitPerSecond)}/second`);
  }
  if (rule.log) parts.push('log prefix "connaro: "');
  if (rule.mark !== undefined) parts.push(`meta mark set ${Math.floor(rule.mark)}`);
  if (rule.dscp !== undefined) parts.push(`ip dscp set ${Math.floor(rule.dscp)}`);
  parts.push(verdict(rule.action));
  return parts.join(' ');
}
